import React, { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { styled } from "@mui/system";
import { format } from "date-fns";
import styles from "./table.module.css";
import { convertToLakhs } from "utils/convertValueIntoLakhs";
import downarrow from "../../../assets/images/arrowDrown.svg";
import uparrow from "../../../assets/images/arrow_up.svg";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "&.MuiTableCell-head": {
    fontFamily: "Unilever Shilling",
    backgroundColor: "#2036C7",
    color: "#fff",
    textAlign: "center",
    padding: "5px",
    fontSize: "12px",
    fontWeight: 400,
    border: "1px solid #EAECF0",
  },
  "&.MuiTableCell-body": {
    fontFamily: "Unilever Shilling",
    fontSize: 12,
    fontWeight: 400,
    padding: "5px",
    color: "#2B2B2B",
    border: "1px solid #EAECF0",
  },
}));

const StyledTableRow = styled(TableRow)({});

const simulateParameters = [
  "Projected Demand for RM (in T)",
  "Projected RM Availability Old (in T)",
  "Projected RM Availability New (in T)",
  "Ending on Hand Old (in T)",
  "Ending on Hand New (in T)",
  "Revenue at Risk Addressed (in INR L)",
];

const demandParameters = [
  "Projected Demand",
  "Opening Stocks",
  "Safety Norm",
  "P2 Alert - Safety Norm vs Opening Stocks",
  "Projected Supply",
  "Ending on Hand",
  // "GRN quantity",
  // "QIC quantity"
];

const DemandTable = ({ data, type, unitOfMeasure, weekLabel }) => {
  const [expandedParameter, setExpandedParameter] = useState(null);
  const baseParameters = type === "simulate" ? simulateParameters : demandParameters;

  // Get current week in the same format as weekLabel
  const currentDate = new Date();
  const currentWeek = format(currentDate, "w");
  const currentWeekLabel = `Week ${currentWeek}`;

  // Determine which P1 Alert to show
  const p1AlertColumn = weekLabel === currentWeekLabel 
    ? "P1 Alert - Demand vs Opening Stocks" 
    : "P1 Alert - Demand VS Projected Supply";

  // Insert the P1 Alert column at the correct position
  const parameters = [
    ...baseParameters.slice(0, 2),
    p1AlertColumn,
    ...baseParameters.slice(2)
  ];

  const parseDate = (dateString) => {
    const [day, month, year] = dateString.split("-");
    return new Date(`${year}-${month}-${day}`);
  };

  const handleParameterClick = (param) => {
    setExpandedParameter((prev) => (prev === param ? null : param));
  };

  const getAdditionalDataKeys = (param) => {
    const additionalData = data[0][param];
    return additionalData && typeof additionalData === "object"
      ? Object.keys(additionalData).filter((key) => key !== "value")
      : [];
  };

  const renderCellValue = (param, value) => {
    if (value == null) return "";

    if (typeof value === "object") {
      return value.hasOwnProperty("value") && typeof value.value === "number"
        ? value.value.toFixed(1)
        : 0;
    }

    if (typeof value === "number") {
      return param === "Revenue Opportunity Loss"
        ? convertToLakhs(value, unitOfMeasure)
        : unitOfMeasure === "T"
          ? Math.round(value)
          : value.toFixed(1);
    }
  }

  const renderRowCells = (param, entry) => {
    return (
      <StyledTableCell
        key={entry.Date}
        className={typeof entry[param] === "object" ? `${styles.fontRow}` : ''}
        sx={{ textAlign: "center" }}
      >
        {param.includes("P1 Alert") || param === "P2 Alert - Safety Norm vs Opening Stocks" ?
          <span style={{ 
            padding: '1px 10px', 
            borderRadius: '6px', 
            backgroundColor: param.includes("P1 Alert") && Math.sign(entry[param]) === -1 ? "#FF9998" : 
                           param === "P2 Alert - Safety Norm vs Opening Stocks" && Math.sign(entry[param]) === -1 ? "#FFEC9A" : "unset" 
          }}>
            {renderCellValue(param, entry[param])}
          </span>
          : renderCellValue(param, entry[param])
        }
      </StyledTableCell>
    );
  };

  return (
    <TableContainer className={styles.demandTable}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <StyledTableCell>Material Description</StyledTableCell>
            {data.map((entry, index) => (
              <StyledTableCell key={index}>
                {weekLabel.includes('-') ? entry.Week :
                  format(parseDate(entry.Date), "d MMM")}
              </StyledTableCell>
            ))
            }
          </TableRow>
        </TableHead>
        <TableBody>
          {parameters.map((param) => (
            <React.Fragment key={param}>
              <StyledTableRow
                className={data[0][param] && typeof data[0][param] === "object" ? styles.expandRow : ""}>
                <StyledTableCell
                  onClick={() => handleParameterClick(param)}
                  className={data[0][param] && typeof data[0][param] === "object" ? `${styles.paramterRow} ${styles.fontRow}` : styles.paramterRow}>
                  {typeof data[0][param] === "object" && (
                    <img
                      src={expandedParameter === param ? downarrow : uparrow}
                      className={styles.plusIcon}
                      alt="toggle"
                    />
                  )}
                  {param.includes("Revenue") ?
                    // || param.includes("GRN quantity") || param.includes("QIC quantity")
                    param : `${param} ( in ${unitOfMeasure})`}
                </StyledTableCell>
                {data.map((entry) => renderRowCells(param, entry))}
              </StyledTableRow>
              {expandedParameter === param &&
                getAdditionalDataKeys(param).map((key) => (
                  <StyledTableRow key={key}>
                    <StyledTableCell><span style={{ marginLeft: "30px" }}>{`${key} ( in ${unitOfMeasure})`}</span></StyledTableCell>
                    {data.map((entry) => (
                      <StyledTableCell key={entry.Date} sx={{ textAlign: "center" }}>
                        {entry[param]?.[key]?.toFixed(1)}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DemandTable;